import useThemeSwitcher from '@app/containers/ThemeSwitcher/context';
import { getCompany } from '@app/helpers/utility';
import { Layout, Tooltip } from 'antd';
import type { CSSProperties } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { FaBars, FaCog, FaLink, FaSearch, FaTimes } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { AllSearch } from './allSearch';
import { TopbarWrapper } from './topbar.style';
import { TopbarAddModules } from './topbarAddModule';
import { TopbarNotification } from './topbarNotification';
import { TopbarTasks } from './topbarTasks';
import { TopbarUser } from './topbarUser';

const { Header } = Layout;
const iconSize = '28px';

interface TopbarCustomProps {
  onMenuClick?: () => void;
}

export const TopbarCustom = ({ onMenuClick }: TopbarCustomProps) => {
  const { topbarTheme } = useThemeSwitcher();
  const { pathname: url } = useLocation();
  // left / width live in topbar.style so they can change on mobile
  const styling: CSSProperties = {
    background: topbarTheme.backgroundColor,
    position: 'fixed',
    zIndex: 50,
  };
  const company = getCompany();

  // Mobile only: search is an icon that opens the box over the topbar
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchOpen) {
      searchRef.current?.querySelector('input')?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    setSearchOpen(false);
  }, [url]);

  return (
    <TopbarWrapper>
      <Header style={styling} className="isomorphicTopbar">
        <div id="topbar-info" className="header-split">
          <div className="logo topbar-left">
            <button
              type="button"
              className="mobile-menu-trigger"
              aria-label="Open menu"
              onClick={onMenuClick}
            >
              <FaBars />
            </button>
            <Link to="/dashboard">
              <h2>{company?.name}</h2>
            </Link>
          </div>
          <div
            className={`topbar-center ${searchOpen ? 'search-open' : ''}`}
            ref={searchRef}
            onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
          >
            <AllSearch />
            <button
              type="button"
              className="mobile-search-close"
              aria-label="Close search"
              onClick={() => setSearchOpen(false)}
            >
              <FaTimes />
            </button>
          </div>
          <div className="topbar-right">
            <ul className="isoRight topbarSystemMenu">
              <li className="mobile-search-trigger">
                <button
                  type="button"
                  aria-label="Search"
                  onClick={() => setSearchOpen(true)}
                >
                  <FaSearch className="topbar-icon" />
                </button>
              </li>
              <li className="add-modules">
                <TopbarAddModules />
              </li>
              <li className="resources">
                <a
                  target="_blank"
                  href="https://www.dropbox.com/sh/g0qgl97adhbv6jm/AAB9cE9hOlvvIjiIT4wi39Bja?dl=0"
                  rel="noreferrer"
                >
                  <Tooltip
                    placement="bottom"
                    title={'Resources for CRM, Sales, Marketing, and more!'}
                  >
                    <FaLink className="topbar-icon" />
                  </Tooltip>
                </a>
              </li>
              <li className="topbar-icon">
                <TopbarTasks />
              </li>
              <li className="isoNotify">
                <TopbarNotification />
              </li>
              <li className="isoUser">
                <TopbarUser />
              </li>
              <li>
                <Link to="/dashboard/settings">
                  <FaCog className="topbar-icon" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </Header>
    </TopbarWrapper>
  );
};
